import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Results as ResultsDisplay } from './Results/Results';
import { Toast } from './Toast/Toast';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { calculateResults } from '../utils/pollUtils';
import type { Poll, VoteResult } from '../types';

export function Results() {
  const { pollId } = useParams<{ pollId: string }>();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const [poll, setPoll] = useState<Poll | null>(null);
  const [results, setResults] = useState<VoteResult[]>([]);
  const [voterCount, setVoterCount] = useState(0);

  useEffect(() => {
    const fetchPollAndResults = async () => {
      if (!pollId) return;
      console.log('Fetching poll with ID:', pollId);

      try {
        // Fetch poll details
        const { data: pollData, error: pollError } = await supabase
          .from('polls')
          .select('*')
          .eq('id', pollId)
          .maybeSingle();

        console.log('Poll data:', pollData);
        console.log('Poll error:', pollError);

        if (pollError) throw pollError;
        if (!pollData) {
          console.log('Poll not found in database');
          throw new Error('Poll not found');
        }

        // Fetch candidates
        let { data: candidates, error: candidatesError } = await supabase
          .from('candidates')
          .select('*')
          .eq('poll_id', pollData.id)
          .order('created_at', { ascending: true });

        if (candidatesError) throw candidatesError;
        if (!candidates) {
          candidates = [];
        }

        // Fetch all votes for this poll
        const { data: votes, error: votesError } = await supabase
          .from('votes')
          .select('*')
          .eq('poll_id', pollData.id);

        if (votesError) throw votesError;

        // Transform votes into the expected format
        const voteMap: Record<string, Record<string, boolean>> = {};
        votes?.forEach(vote => {
          if (!voteMap[vote.voter_id]) {
            voteMap[vote.voter_id] = {};
          }
          voteMap[vote.voter_id][vote.candidate_id] = vote.vote;
        });
        
        // Set initial voter count
        setVoterCount(votes ? new Set(votes.map(v => v.voter_id)).size : 0);

        const fullPoll: Poll = {
          ...pollData,
          candidates,
          votes: voteMap
        };

        setPoll(fullPoll);
        setResults(calculateResults(fullPoll));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load poll results';
        console.error('Error fetching poll and results:', error, message);
        showToast(message);
        navigate('/');
      }
    };

    fetchPollAndResults();
  }, [pollId, navigate, showToast]);

  const handleVoteChange = async (candidateId: string, vote: boolean) => {
    if (!poll || !userId || !pollId) return;

    try {
      const { error } = await supabase
        .from('votes')
        .upsert({
          voter_id: userId,
          candidate_id: candidateId,
          poll_id: pollId,
          vote
        });

      if (error) throw error;

      // Update local state
      setVoterCount(prev => {
        const voters = new Set([...Array.from(prev), userId]);
        return voters.size;
      });
      
      setPoll(prev => {
        if (!prev) return null;
        const newVotes = { ...prev.votes };
        if (!newVotes[userId]) {
          newVotes[userId] = {};
        }
        newVotes[userId][candidateId] = vote;
        return { ...prev, votes: newVotes };
      });

      // Refetch results to ensure consistency
      const { data: votes, error: votesError } = await supabase
        .from('votes')
        .select('*')
        .eq('poll_id', pollId);

      if (votesError) throw votesError;

      if (poll && votes) {
        const voteMap: Record<string, Record<string, boolean>> = {};
        votes.forEach(vote => {
          if (!voteMap[vote.voter_id]) {
            voteMap[vote.voter_id] = {};
          }
          voteMap[vote.voter_id][vote.candidate_id] = vote.vote;
        });

        const updatedPoll = { ...poll, votes: voteMap };
        setPoll(updatedPoll);
        setResults(calculateResults(updatedPoll));
      }
    } catch (error) {
      console.error('Error updating vote:', error);
      showToast('Failed to update vote');
    }
  };

  const handleUpdateSettings = async (settings: Partial<Poll['settings']>) => {
    if (!poll) return;

    try {
      const { error } = await supabase
        .from('polls')
        .update({ settings: { ...poll.settings, ...settings } })
        .eq('id', poll.id);

      if (error) throw error;

      setPoll(prev => prev ? {
        ...prev,
        settings: { ...prev.settings, ...settings }
      } : null);
    } catch (error) {
      console.error('Error updating poll settings:', error);
      showToast('Failed to update poll settings');
    }
  };

  const handleNominateCandidate = async (names: string[]) => {
    if (!poll || !pollId) return;

    try {
      const candidateRecords = names.map(name => ({
        poll_id: pollId,
        name: name.trim()
      }));

      const { data: newCandidates, error: candidatesError } = await supabase
        .from('candidates')
        .insert(candidateRecords)
        .select();

      if (candidatesError) throw candidatesError;
      if (!newCandidates) throw new Error('Failed to create candidates');

      // Update local state
      setPoll(prev => {
        if (!prev) return null;
        return {
          ...prev,
          candidates: [...prev.candidates, ...newCandidates]
        };
      });

      setResults(prev => {
        const newResults = newCandidates.map(candidate => ({
          candidateId: candidate.id,
          candidateName: candidate.name,
          upvotes: 0,
          downvotes: 0,
          total: 0
        }));
        return [...prev, ...newResults];
      });

      showToast('New candidates added successfully!');
    } catch (error) {
      console.error('Error nominating candidates:', error);
      showToast('Failed to add new candidates');
    }
  };

  if (!poll) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      {results.length > 0 ? (
        <ResultsDisplay
          poll={poll}
          results={results}
          userId={userId}
          isAuthor={poll.author_id === userId}
          onVoteChange={handleVoteChange}
          onNominateCandidate={handleNominateCandidate}
          onUpdateSettings={handleUpdateSettings}
        />
      ) : (
        <div className="container mx-auto px-4 py-8">
          <div className="w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">No Candidates Yet</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This poll doesn't have any candidates yet.
              {poll.settings.allowNominations && (
                <> You can be the first to nominate candidates!</>
              )}
            </p>
            {poll.settings.allowNominations && (
              <button
                onClick={() => handleNominateCandidate([''])}
                className="inline-block bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
              >
                Nominate Candidates
              </button>
            )}
          </div>
        </div>
      )}
      {toast && <Toast message={toast} onDismiss={hideToast} />}
    </div>
  );
}